import {useSearchParams} from 'react-router-dom'
import {AUTO_ASSIGN_WIZARD_STEP_COUNT, AutoAssignRuleType, AutoAssignWizardStep} from '@/types/auto-assign-rule'
import type {AutoAssignWizardStep as Step} from '@/types/auto-assign-rule'

const PARAM_MODE = 'mode'
const PARAM_RULE_ID = 'ruleId'
const PARAM_TYPE = 'type'
const PARAM_STEP = 'step'
const MODE_UPDATE = 'update'

export interface AutoAssignWizardModeState {
    /** True when the URL search params carry mode=update and a ruleId — the wizard should preload form state from an existing rule. */
    isUpdateFromUrl: boolean
    /** Resolved rule id when {@link isUpdateFromUrl} is true. */
    ruleId: string | null
    /** Initial rule type seeded from the type search param; defaults to ACCESS_PROFILE when omitted. */
    seededRuleType: AutoAssignRuleType
    /** Initial wizard step seeded from the step search param; clamped to [0, 4]. Default 0 (BASICS). */
    initialStep: Step
}

function parseRuleType(value: string | null): AutoAssignRuleType {
    if (value === AutoAssignRuleType.FUNCTION_GROUP) return AutoAssignRuleType.FUNCTION_GROUP
    return AutoAssignRuleType.ACCESS_PROFILE
}

function parseStep(value: string | null): Step {
    if (value === null) return AutoAssignWizardStep.BASICS
    const parsed = Number.parseInt(value, 10)
    if (!Number.isFinite(parsed) || parsed < 0) return AutoAssignWizardStep.BASICS
    if (parsed >= AUTO_ASSIGN_WIZARD_STEP_COUNT) return AutoAssignWizardStep.BASICS
    return parsed as Step
}

/**
 * URL-driven dispatch for the AutoAssignRuleRequestCreateWizard.
 *
 * Mirrors the shape of {@code useJobBuilderMode} from the jobs feature: the
 * page never reads {@code searchParams} directly; this hook centralises the
 * parsing rules, defaults, and clamping so the URL surface area is testable
 * in isolation.
 *
 * Supported search params:
 *  - mode=update + ruleId=&lt;UUID&gt; — preload from an existing rule.
 *  - type=ACCESS_PROFILE | FUNCTION_GROUP — seed the rule-type toggle.
 *  - step=&lt;0..4&gt; — initial wizard step.
 *
 * Browser refresh restores step + type seeding from the URL but resets form
 * fields — see the URL-state-design note on the issue.
 */
export function useAutoAssignRuleRequestWizardMode(): AutoAssignWizardModeState {
    const [searchParams] = useSearchParams()
    const mode = searchParams.get(PARAM_MODE)
    const ruleId = searchParams.get(PARAM_RULE_ID)
    const isUpdateFromUrl = mode === MODE_UPDATE && !!ruleId

    return {
        isUpdateFromUrl,
        ruleId: isUpdateFromUrl ? ruleId : null,
        seededRuleType: parseRuleType(searchParams.get(PARAM_TYPE)),
        initialStep: parseStep(searchParams.get(PARAM_STEP)),
    }
}
